import { useEffect, useRef } from 'react';

import { isSpaceKey, isTextInputFocused } from '../../hooks/useClickerControls';
import { TTSState } from '../../hooks/useTextToSpeech';
import { useRegisterClickerLiveState } from '../../providers/LiveLessonClickerProvider';

// Space plays and pauses the read-aloud while it runs, like every media
// player. In a live lesson it also tells the clicker to leave Space alone
// (isReadAloudActive) — otherwise Space closes the passage and the next
// press skips to the next slide.
// The volume slider keeps focus after a drag, but it's no text field —
// Space should still pause.
const isTypingInTextField = () => {
  const el = document.activeElement;
  return isTextInputFocused() && !(el instanceof HTMLInputElement && el.type === 'range');
};

export function useSpaceTogglesPlayback(state: TTSState, togglePlayback: () => void) {
  const isActive = state === 'playing' || state === 'paused' || state === 'loading';
  useRegisterClickerLiveState({ isReadAloudActive: isActive }, ['isReadAloudActive']);

  const togglePlaybackRef = useRef(togglePlayback);
  useEffect(() => {
    togglePlaybackRef.current = togglePlayback;
  });

  useEffect(() => {
    if (!isActive) return;
    const handler = (e: KeyboardEvent) => {
      if (!isSpaceKey(e) || isTypingInTextField()) return;
      // Also keeps Space from clicking whatever button has focus, and from
      // reaching other Space shortcuts (e.g. the designer's hold-to-pan).
      e.preventDefault();
      e.stopPropagation();
      // Nothing to toggle until the audio has loaded.
      if (e.repeat || state === 'loading') return;
      togglePlaybackRef.current();
    };
    window.addEventListener('keydown', handler, true);
    return () => window.removeEventListener('keydown', handler, true);
  }, [isActive, state]);
}
